// PatientQRLanding.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../components/firebase-config.js";
import {
    Container, Row, Col, Form, Alert, Spinner, Modal, InputGroup, Button
  } from "react-bootstrap";
  
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus, faCalendarPlus, faCheck, faTimes,
  faSpinner, faExclamationTriangle, faClock,
  faUser, faEnvelope, faPhone, faCalendarAlt,
  faInfoCircle, faQrcode, faHospital, faUserMd,
  faSearch, faTicketAlt
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import styled from "styled-components";

// Réutiliser les styles de votre application
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem 0;
  
  @media (max-width: 768px) {
    padding: 0;
    background: #ffffff;
  }
`;

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    border-radius: 16px;
    margin-bottom: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, #f8f9fa, white);
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    
    h5 {
      margin-bottom: 0.5rem;
    }
  }
`;

const CardBody = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StyledButton = styled(motion.button)`
  border-radius: 16px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  
  &.primary {
    background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
    background-size: 200% 200%;
    color: white;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.2);
    
    &:hover {
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #4b5563;
    
    &:hover {
      background: #e9ecef;
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 12px;
    
    &:hover {
      transform: none;
    }
  }
  
  &.mobile-full {
    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
    }
  }
`;

const VerificationCode = styled.div`
  background: #f3f4f6;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  margin: 2rem 0;
  font-size: 1.5rem;
  font-weight: bold;
  letter-spacing: 2px;
`;

// Nouveau style pour le formulaire de vérification
const VerificationForm = styled.div`
  background: #f9fafb;
  border-radius: 16px;
  padding: 1.5rem;
  margin-top: 2rem;
  border: 1px dashed #d1d5db;
  
  h5 {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 0.75rem;
      color: #6366f1;
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CodeInput = styled(Form.Control)`
  font-size: 1.5rem;
  letter-spacing: 4px;
  text-align: center;
  font-weight: bold;
  padding: 1rem;
  border-radius: 12px;
  
  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 0.25rem rgba(99, 102, 241, 0.25);
  }
`;

const PatientQRLanding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [qrData, setQrData] = useState(null);
  const [auxiliaireData, setAuxiliaireData] = useState(null);
  const [structureData, setStructureData] = useState(null);
  const [medecins, setMedecins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Info, 2: Enregistrement patient, 3: RDV, 4: Confirmation
  
  // États pour le formulaire patient
  const [patientForm, setPatientForm] = useState({
    nom: "",
    dateNaissance: "",
    email: "",
    telephone: "",
    assurances: []
  });
  
  // États pour le formulaire de rendez-vous
  const [rdvForm, setRdvForm] = useState({
    medecinId: "",
    heure: "",
    motif: "",
    statut: "confirmé",
    duree: "15"  // Ajouter une durée par défaut de 15 minutes
  });
  
  // État pour le code de vérification
  const [verificationCode, setVerificationCode] = useState("");
  const [orderNumber, setOrderNumber] = useState(null);
  
  // Créneaux disponibles
  const [creneauxDisponibles, setCreneauxDisponibles] = useState([]);
  
  // États pour la vérification de rendez-vous
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCodeInput, setVerificationCodeInput] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  useEffect(() => {
    const fetchQRData = async () => {
      try {
        const qrId = searchParams.get('qr');
        
        if (!qrId) {
          setError("Code QR invalide ou manquant.");
          setLoading(false);
          return;
        }
        
        // Récupérer les données du code QR
        const qrDocRef = doc(db, "qrCodes", qrId);
        const qrDocSnap = await getDoc(qrDocRef);
        
        if (!qrDocSnap.exists()) {
          setError("Ce code QR n'existe pas ou a expiré.");
          setLoading(false);
          return;
        }
        
        const qrDataFromDB = qrDocSnap.data();
        
        // Vérifier si le code QR est toujours valide
        if (!qrDataFromDB.active || qrDataFromDB.expiresAt.toDate() < new Date()) {
          setError("Ce code QR a expiré.");
          setLoading(false);
          return;
        }
        
        setQrData(qrDataFromDB);
        
        // Récupérer les données de l'auxiliaire
        const auxiliaireDocRef = doc(db, "auxiliaires", qrDataFromDB.auxiliaireId);
        const auxiliaireDocSnap = await getDoc(auxiliaireDocRef);
        
        if (!auxiliaireDocSnap.exists()) {
          setError("Impossible de trouver les informations de l'auxiliaire.");
          setLoading(false);
          return;
        }
        
        const auxiliaireDataFromDB = auxiliaireDocSnap.data();
        setAuxiliaireData(auxiliaireDataFromDB);
        
        // Vérifier si l'auxiliaire est dans ses heures de travail
        const estDansHoraireTravail = verifierHoraireTravail(auxiliaireDataFromDB);
        
        if (!estDansHoraireTravail) {
          setError("Ce service n'est pas disponible en dehors des heures de travail de l'auxiliaire.");
          setLoading(false);
          return;
        }
        
        // Récupérer les données de la structure
        const structureDocRef = doc(db, "structures", qrDataFromDB.structureId);
        const structureDocSnap = await getDoc(structureDocRef);
        
        if (structureDocSnap.exists()) {
          setStructureData(structureDocSnap.data());
        }
        
        // Récupérer les médecins
        if (qrDataFromDB.medecinIds && qrDataFromDB.medecinIds.length > 0) {
          const medecinsData = [];
          for (const medecinId of qrDataFromDB.medecinIds) {
            const medecinDoc = await getDoc(doc(db, "medecins", medecinId));
            if (medecinDoc.exists()) {
              medecinsData.push({ id: medecinId, ...medecinDoc.data() });
            }
          }
          setMedecins(medecinsData);
        }
        
        // Générer un code de vérification aléatoire à 6 chiffres
        const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
        setVerificationCode(randomCode);
        
        setLoading(false);
      } catch (err) {
        setError(`Erreur: ${err.message}`);
        setLoading(false);
      }
    };
    
    fetchQRData();
  }, [searchParams]);
  
  // Fonction pour vérifier si l'auxiliaire est dans ses heures de travail
  const verifierHoraireTravail = (auxData) => {
    if (!auxData || !auxData.horaires) {
      return true; // Si aucun horaire n'est défini, permettre l'accès
    }
    
    const maintenant = new Date();
    const joursMap = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
    const jourSemaine = joursMap[maintenant.getDay()];
    const jourSemaineCapitalized = jourSemaine.charAt(0).toUpperCase() + jourSemaine.slice(1);
    
    // Vérifier si l'auxiliaire travaille aujourd'hui
    let horaireAujourdhui = null;
    
    // Vérifier si les horaires sont stockés sous forme de tableau
    if (Array.isArray(auxData.horaires)) {
      horaireAujourdhui = auxData.horaires.find(
        horaire => {
          if (!horaire.jour) return false;
          const jourHoraire = horaire.jour.toLowerCase();
          return jourHoraire === jourSemaine || jourHoraire === jourSemaineCapitalized.toLowerCase();
        }
      );
      
      if (!horaireAujourdhui) {
        return false;
      }
      
      // Convertir l'heure actuelle en minutes depuis minuit
      const heureActuelle = maintenant.getHours();
      const minutesActuelles = maintenant.getMinutes();
      const minutesDepuisMinuit = heureActuelle * 60 + minutesActuelles;
      
      // Convertir les heures de début et de fin en minutes depuis minuit
      const [heureDebutH, heureDebutM] = (horaireAujourdhui.heureDebut || "08:00").split(':').map(Number);
      const [heureFinH, heureFinM] = (horaireAujourdhui.heureFin || "18:00").split(':').map(Number);
      
      const minutesDebut = heureDebutH * 60 + heureDebutM;
      const minutesFin = heureFinH * 60 + heureFinM;
      
      // Vérifier si l'heure actuelle est dans la plage de travail
      return minutesDepuisMinuit >= minutesDebut && minutesDepuisMinuit <= minutesFin;
    } 
    // Si les horaires sont stockés sous forme d'objet
    else if (typeof auxData.horaires === 'object') {
      horaireAujourdhui = auxData.horaires[jourSemaine];
      
      if (!horaireAujourdhui || !horaireAujourdhui.actif) {
        return false;
      }
      
      // Convertir l'heure actuelle en minutes depuis minuit
      const heureActuelle = maintenant.getHours();
      const minutesActuelles = maintenant.getMinutes();
      const minutesDepuisMinuit = heureActuelle * 60 + minutesActuelles;
      
      // Convertir les heures de début et de fin en minutes depuis minuit
      const [heureDebutH, heureDebutM] = (horaireAujourdhui.debut || "08:00").split(':').map(Number);
      const [heureFinH, heureFinM] = (horaireAujourdhui.fin || "18:00").split(':').map(Number);
      
      const minutesDebut = heureDebutH * 60 + heureDebutM;
      const minutesFin = heureFinH * 60 + heureFinM;
      
      // Vérifier si l'heure actuelle est dans la plage de travail
      return minutesDepuisMinuit >= minutesDebut && minutesDepuisMinuit <= minutesFin;
    }
    
    return true;
  };
  
  // Fonction pour vérifier les créneaux disponibles pour un médecin aujourd'hui
  const checkCreneauxDisponibles = async (medecinId) => {
    if (!medecinId) return [];
    
    const medecin = medecins.find(m => m.id === medecinId);
    if (!medecin) return [];
    
    // Déterminer le jour de la semaine
    const jourDate = new Date();
    const joursMap = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
    const jourSemaine = joursMap[jourDate.getDay()];
    
    // Date d'aujourd'hui au format YYYY-MM-DD
    const today = jourDate.toISOString().split("T")[0];
    
    // Vérifier si le médecin travaille aujourd'hui
    let heureDebut = "";
    let heureFin = "";
    
    if (Array.isArray(medecin.disponibilites)) {
      const disponibiliteJour = medecin.disponibilites.find(
        d => d.jour.toLowerCase() === jourSemaine.toLowerCase()
      );
      
      if (disponibiliteJour) {
        heureDebut = disponibiliteJour.heureDebut;
        heureFin = disponibiliteJour.heureFin;
      } else {
        return []; // Le médecin ne travaille pas aujourd'hui
      }
    } else if (medecin.disponibilites && medecin.disponibilites[jourSemaine]) {
      if (!medecin.disponibilites[jourSemaine].actif) {
        return []; // Le médecin ne travaille pas aujourd'hui
      }
      heureDebut = medecin.disponibilites[jourSemaine].debut;
      heureFin = medecin.disponibilites[jourSemaine].fin;
    } else {
      return []; // Pas de disponibilités pour aujourd'hui
    }
    
    // Récupérer les rendez-vous existants pour ce médecin aujourd'hui
    const rdvQuery = query(
      collection(db, "rendezvous"),
      where("medecinId", "==", medecinId),
      where("date", "==", today),
      where("statut", "!=", "annulé")
    );
    const rdvSnapshot = await getDocs(rdvQuery);
    const rdvDuJour = [];
    rdvSnapshot.forEach((doc) => {
      rdvDuJour.push(doc.data());
    });
    
    // Générer des créneaux de 15 minutes
    const creneaux = [];
    let heure = heureDebut;
    
    // Nombre maximum de rendez-vous simultanés autorisés
    const MAX_RDV_SIMULTANES = 3;
    
    while (heure < heureFin) {
      const [h, m] = heure.split(":").map(Number);
      const heureFinCreneau = new Date();
      heureFinCreneau.setHours(h, m + parseInt(rdvForm.duree), 0, 0);
      const heureFinStr = `${heureFinCreneau.getHours().toString().padStart(2, "0")}:${heureFinCreneau.getMinutes().toString().padStart(2, "0")}`;
      
      if (heureFinStr <= heureFin) {
        // Compter le nombre de rendez-vous à cette heure
        const rdvAuCreneau = rdvDuJour.filter(rdv => rdv.heure === heure);
        const nombreRdvAuCreneau = rdvAuCreneau.length;
        
        // Déterminer le statut du créneau
        let statut = "disponible";
        if (nombreRdvAuCreneau >= MAX_RDV_SIMULTANES) {
          statut = "complet";
        } else if (nombreRdvAuCreneau > 0) {
          statut = "partiel";
        }
        
        // Vérifier si ce créneau est déjà passé aujourd'hui
        const maintenant = new Date();
        const heureActuelle = maintenant.getHours();
        const minuteActuelle = maintenant.getMinutes();
        
        const estPasse = (heureActuelle > h) || (heureActuelle === h && minuteActuelle > m);
        
        if (!estPasse) {
          creneaux.push({
            heure: heure,
            statut: statut,
            nombreRdv: nombreRdvAuCreneau,
            estDisponible: statut !== "complet"
          });
        }
      }
      
      // Passer au créneau suivant (par tranches de 15 minutes)
      const nextHeure = new Date();
      nextHeure.setHours(h, m + 15, 0, 0);
      heure = `${nextHeure.getHours().toString().padStart(2, "0")}:${nextHeure.getMinutes().toString().padStart(2, "0")}`;
    }
    
    return creneaux;
  };
  
  // Fonction pour gérer les changements dans le formulaire de patient
  const handlePatientChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "assurances") {
      const updatedArray = [...patientForm.assurances];
      if (checked) {
        updatedArray.push(value);
      } else {
        const index = updatedArray.indexOf(value);
        if (index > -1) {
          updatedArray.splice(index, 1);
        }
      }
      setPatientForm({ ...patientForm, [name]: updatedArray });
    } else {
      setPatientForm({ ...patientForm, [name]: value });
    }
  };
  
  // Fonction pour gérer les changements dans le formulaire de rendez-vous
  const handleRdvChange = (e) => {
    const { name, value } = e.target;
    setRdvForm({ ...rdvForm, [name]: value });
  };
  
  // Fonction pour gérer le changement de médecin dans le formulaire de rendez-vous
  const handleMedecinChange = async (medecinId) => {
    setRdvForm({
      ...rdvForm,
      medecinId,
      heure: "" // Réinitialiser l'heure sélectionnée
    });
    
    // Mettre à jour les créneaux disponibles
    const creneaux = await checkCreneauxDisponibles(medecinId);
    setCreneauxDisponibles(creneaux);
  };
  
  // Fonction pour soumettre le formulaire de patient
  const handlePatientSubmit = (e) => {
    e.preventDefault();
    setStep(3); // Passer à l'étape de prise de rendez-vous
  };
  
  // Fonction pour soumettre le formulaire de rendez-vous
  const handleRdvSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Date d'aujourd'hui au format YYYY-MM-DD
      const today = new Date().toISOString().split("T")[0];
      
      // Ajouter le patient à la base de données
      const patientData = {
        ...patientForm,
        structureId: qrData.structureId,
        createdAt: Timestamp.now(),
        createdBy: {
          id: qrData.auxiliaireId,
          type: "auxiliaire",
          nom: ` ${auxiliaireData.nom}`
        }
      };
      
      const patientDocRef = await addDoc(collection(db, "patients"), patientData);
      
      // Récupérer les informations du médecin
      const medecin = medecins.find(m => m.id === rdvForm.medecinId);
      
      // Ajouter le rendez-vous à la base de données
      const rdvData = {
        ...rdvForm,
        patientId: patientDocRef.id,
        date: today,
        structureId: qrData.structureId,
        medecinNom: medecin ? `${medecin.titre || "Dr"} ${medecin.nom}` : "",
        medecinSpecialite: medecin ? medecin.specialite : "",
        patientNom: `${patientForm.nom}`,
        patientTelephone: patientForm.telephone,
        patientEmail: patientForm.email,
        createdAt: Timestamp.now(),
        createdBy: {
          id: qrData.auxiliaireId,
          type: "auxiliaire",
          nom: ` ${auxiliaireData.nom}`
        },
        priorite: "normal",
        verificationCode: verificationCode,
        qrCodeId: searchParams.get('qr')
      };
      
      const rdvDocRef = await addDoc(collection(db, "rendezvous"), rdvData);
      
      // Récupérer le numéro d'ordre pour ce rendez-vous
      const rdvAnterieurQuery = query(
        collection(db, "rendezvous"),
        where("date", "==", today),
        where("medecinId", "==", rdvForm.medecinId),
        where("heure", "<=", rdvForm.heure),
        where("statut", "!=", "annulé")
      );
      
      const rdvAnterieurSnapshot = await getDocs(rdvAnterieurQuery);
      setOrderNumber(rdvAnterieurSnapshot.size);
      
      setLoading(false);
      setStep(4); // Passer à l'étape de confirmation
    } catch (error) {
      setError(`Erreur: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Fonction pour vérifier un rendez-vous avec le code
  const verifierRendezVous = async () => {
    if (!verificationCodeInput || verificationCodeInput.length !== 6) {
      setVerificationResult({
        success: false,
        message: "Veuillez entrer un code de vérification valide à 6 chiffres."
      });
      return;
    }
    
    try {
      setVerificationLoading(true);
      
      // Date d'aujourd'hui au format YYYY-MM-DD
      const today = new Date().toISOString().split("T")[0];
      
      // Rechercher le rendez-vous avec ce code de vérification
      const rdvQuery = query(
        collection(db, "rendezvous"),
        where("verificationCode", "==", verificationCodeInput),
        where("date", "==", today),
        where("structureId", "==", qrData.structureId)
      );
      
      const rdvSnapshot = await getDocs(rdvQuery);
      
      if (rdvSnapshot.empty) {
        setVerificationResult({
          success: false,
          message: "Aucun rendez-vous trouvé avec ce code de vérification pour aujourd'hui."
        });
      } else {
        // Récupérer les données du rendez-vous
        let rdvData = null;
        rdvSnapshot.forEach((doc) => {
          rdvData = { id: doc.id, ...doc.data() };
        });
        
        setVerificationResult({
          success: true,
          message: "Rendez-vous vérifié avec succès!",
          rendezVous: rdvData
        });
      }
      
      setVerificationLoading(false);
    } catch (error) {
      setVerificationResult({
        success: false,
        message: `Erreur lors de la vérification: ${error.message}`
      });
      setVerificationLoading(false);
    }
  };
  
  // Composant pour les créneaux horaires
  const TimeSlotGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    margin-top: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `;

  const TimeSlot = styled(motion.div)`
    padding: 1rem;
    border-radius: 16px;
    text-align: center;
    cursor: pointer;
    position: relative;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &.disponible {
      background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
      color: #059669;
      border: 1px solid #a7f3d0;
    }
    
    &.partiel {
      background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
      color: #d97706;
      border: 1px solid #fcd34d;
    }
    
    &.complet {
      background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
      color: #6b7280;
      border: 1px solid #e5e7eb;
      cursor: not-allowed;
      opacity: 0.7;
    }
    
    &.selected {
      border: 2px solid #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    }
    
    .slot-info {
      font-size: 0.8rem;
      margin-top: 0.5rem;
      opacity: 0.9;
    }
    
    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
      border-radius: 12px;
      font-size: 0.9rem;
      
      .slot-info {
        font-size: 0.7rem;
      }
    }
  `;
  
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.div
                       animate={{ 
              rotate: [0, 360],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "linear" 
            }}
          >
            <FontAwesomeIcon icon={faSpinner} className="text-primary" size="3x" />
          </motion.div>
          <motion.h5 
            className="text-muted mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Chargement en cours...
          </motion.h5>
        </motion.div>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className="mt-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Alert variant="danger" className="p-4 text-center rounded-4 shadow">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 15
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="mb-3" size="3x" />
            </motion.div>
            <h4 className="mt-3">Erreur</h4>
            <p className="mb-0">{error}</p>
            <div className="mt-3">
              <StyledButton 
                className="secondary"
                onClick={() => navigate("/")}
              >
                Retour à l'accueil
              </StyledButton>
            </div>
          </Alert>
        </motion.div>
      </Container>
    );
  }
  
  // Modal pour la vérification de rendez-vous
  const renderVerificationModal = () => (
    <Modal 
      show={showVerificationModal} 
      onHide={() => {
        setShowVerificationModal(false);
        setVerificationCodeInput("");
        setVerificationResult(null);
      }}
      centered
      size="lg"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>
          <FontAwesomeIcon icon={faTicketAlt} className="me-2 text-primary" />
          Vérification de rendez-vous
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <p className="text-muted mb-4">
          Entrez le code de vérification à 6 chiffres fourni lors de la prise de rendez-vous.
        </p>
        
        <InputGroup className="mb-4">
          <CodeInput
            type="text"
            placeholder="000000"
            value={verificationCodeInput}
            onChange={(e) => setVerificationCodeInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            maxLength={6}
          />
          <Button 
            variant="primary" 
            onClick={verifierRendezVous}
            disabled={verificationLoading || verificationCodeInput.length !== 6}
            className="rounded-end"
          >
            {verificationLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
            <span className="ms-2">Vérifier</span>
          </Button>
        </InputGroup>
        
        {verificationResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert 
              variant={verificationResult.success ? "success" : "danger"}
              className="mb-0"
            >
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={verificationResult.success ? faCheck : faExclamationTriangle} 
                  className="me-3 fs-4" 
                />
                <div>
                  <p className="mb-0 fw-bold">{verificationResult.message}</p>
                  
                  {verificationResult.success && verificationResult.rendezVous && (
                    <div className="mt-3">
                      <h5 className="mb-3">Détails du rendez-vous:</h5>
                      <Row>
                        <Col md={6} className="mb-2">
                          <strong>Patient:</strong> {verificationResult.rendezVous.patientNom}
                        </Col>
                        <Col md={6} className="mb-2">
                        <strong>Médecin:</strong> {verificationResult.rendezVous.medecinNom}
                        </Col>
                        <Col md={6} className="mb-2">
                          <strong>Date:</strong> {verificationResult.rendezVous.date}
                        </Col>
                        <Col md={6} className="mb-2">
                          <strong>Heure:</strong> {verificationResult.rendezVous.heure}
                        </Col>
                        <Col md={12} className="mb-2">
                          <strong>Statut:</strong> {verificationResult.rendezVous.statut}
                        </Col>
                        {verificationResult.rendezVous.motif && (
                          <Col md={12} className="mb-2">
                            <strong>Motif:</strong> {verificationResult.rendezVous.motif}
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}
                </div>
              </div>
            </Alert>
          </motion.div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="secondary" onClick={() => {
          setShowVerificationModal(false);
          setVerificationCodeInput("");
          setVerificationResult(null);
        }}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
  
  return (
    <PageWrapper>
      <Container className="py-5">
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StyledCard>
              <CardHeader>
                <h4 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon={faQrcode} className="me-2 text-primary" />
                  Prise de rendez-vous
                </h4>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 300,
                      damping: 15
                    }}
                    className="mb-3"
                  >
                    <FontAwesomeIcon icon={faHospital} size="3x" className="text-primary" />
                  </motion.div>
                  <h3>{structureData?.nom || "Cabinet médical"}</h3>
                  <p className="text-muted">
                    {structureData?.adresse || ""}
                  </p>
                </div>
                
                <Alert variant="info" className="mb-4">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-3 fs-4" />
                    <div>
                      <p className="mb-0">
                        Bienvenue sur notre système de prise de rendez-vous. Pour continuer, vous devrez:
                      </p>
                      <ol className="mt-2 mb-0">
                        <li>Renseigner vos informations personnelles</li>
                        <li>Choisir un médecin et un créneau horaire</li>
                        <li>Recevoir votre numéro d'ordre et code de vérification</li>
                      </ol>
                    </div>
                  </div>
                </Alert>
                
                <div className="d-flex justify-content-between flex-wrap">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                    <StyledButton 
                      className="secondary"
                      onClick={() => setShowVerificationModal(true)}
                    >
                      <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
                      Vérifier un rendez-vous
                    </StyledButton>
                  </motion.div>
                  
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                    <StyledButton 
                      className="primary"
                      onClick={() => setStep(2)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                      Prendre rendez-vous
                    </StyledButton>
                  </motion.div>
                </div>
              </CardBody>
            </StyledCard>
          </motion.div>
        )}
        
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StyledCard>
              <CardHeader>
                <h4 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon={faUserPlus} className="me-2 text-primary" />
                  Vos informations
                </h4>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handlePatientSubmit}>
                  <Row>
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Téléphone <span className="text-danger">(Obligatoire)</span></Form.Label>
                        <Form.Control
                          type="tel"
                          name="telephone"
                          value={patientForm.telephone}
                          onChange={handlePatientChange}
                          required
                          className="rounded-pill py-2 shadow-sm"
                          placeholder="Numéro de téléphone"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Prénom et nom du patient (Optionnel)</Form.Label>
                        <Form.Control
                          type="text"
                          name="nom"
                          value={patientForm.nom}
                          onChange={handlePatientChange}
                          className="rounded-pill py-2 shadow-sm"
                          placeholder="Nom de famille"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Date de naissance (Optionnel)</Form.Label>
                        <Form.Control
                          type="date"
                          name="dateNaissance"
                          value={patientForm.dateNaissance}
                          onChange={handlePatientChange}
                          className="rounded-pill py-2 shadow-sm"
                        />
                      </Form.Group>
                    </Col>
                    <Col md= {6} className="mb-4">
                    <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Email (Optionnel)</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={patientForm.email}
                      onChange={handlePatientChange}
                      className="rounded-pill py-2 shadow-sm"
                      placeholder="Adresse email"
                    />
                  </Form.Group>
                  </Col>
                  </Row>
                  
                 
                  
                  <div className="d-flex justify-content-between flex-wrap">
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                      <StyledButton 
                        type="button"
                        className="secondary"
                        onClick={() => setStep(1)}
                      >
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Retour
                      </StyledButton>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                      <StyledButton 
                        type="submit"
                        className="primary"
                      >
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Continuer
                      </StyledButton>
                    </motion.div>
                  </div>
                </Form>
              </CardBody>
            </StyledCard>
          </motion.div>
        )}
        
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StyledCard>
              <CardHeader>
                <h4 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon={faCalendarPlus} className="me-2 text-primary" />
                  Prendre rendez-vous
                </h4>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleRdvSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Médecin <span className="text-danger">(Obligatoire)</span></Form.Label>
                    <Form.Select
  name="medecinId"
  value={rdvForm.medecinId}
  onChange={(e) => handleMedecinChange(e.target.value)}
  required
  className="rounded-pill py-2 shadow-sm"
>
  <option value="">Sélectionner un médecin</option>
  {medecins.map((medecin) => (
    <option key={medecin.id} value={medecin.id}>
      {medecin.titre || "Dr"} {medecin.nom} - {medecin.specialite}
    </option>
  ))}
</Form.Select>

                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Créneau horaire</Form.Label>
                    {rdvForm.medecinId ? (
                      creneauxDisponibles.length > 0 ? (
                        <TimeSlotGrid>
                          {creneauxDisponibles.map((creneau) => (
                            <TimeSlot
                              key={creneau.heure}
                              className={`${creneau.statut} ${
                                rdvForm.heure === creneau.heure ? "selected" : ""
                              }`}
                              onClick={() => {
                                if (creneau.estDisponible) {
                                  setRdvForm({
                                    ...rdvForm,
                                    heure: creneau.heure,
                                  });
                                }
                              }}
                              whileHover={creneau.estDisponible ? { scale: 1.05, y: -3 } : {}}
                              whileTap={creneau.estDisponible ? { scale: 0.95 } : {}}
                              transition={{ duration: 0.2 }}
                            >
                              {creneau.heure}
                              {creneau.statut === "partiel" && (
                                <div className="slot-info">
                                  {creneau.nombreRdv}/3 occupé
                                  {creneau.nombreRdv > 1 ? "s" : ""}
                                </div>
                              )}
                              {creneau.statut === "complet" && (
                                <div className="slot-info">Complet</div>
                              )}
                            </TimeSlot>
                          ))}
                        </TimeSlotGrid>
                      ) : (
                        <Alert variant="warning" className="rounded-4 border-0 shadow-sm">
                          <div className="d-flex align-items-center">
                            <motion.div
                              animate={{ 
                                rotate: [0, 10, -10, 10, 0],
                              }}
                              transition={{ 
                                duration: 0.5,
                                repeat: 1,
                                repeatDelay: 1
                              }}
                              className="me-3"
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} className="fs-4" />
                            </motion.div>
                            <div>
                              <strong>Aucun créneau disponible</strong>
                              <p className="mb-0">Ce médecin n'a pas de disponibilité pour aujourd'hui.</p>
                            </div>
                          </div>
                        </Alert>
                      )
                    ) : (
                      <Alert variant="info" className="rounded-4 border-0 shadow-sm">
                        <div className="d-flex align-items-center">
                          <motion.div
                            animate={{ y: [0, -5, 0] }}
                            transition={{ 
                              duration: 1,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                            className="me-3"
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} className="fs-4" />
                          </motion.div>
                          <div>
                            <strong>Sélectionnez un médecin</strong>
                            <p className="mb-0">Les créneaux disponibles s'afficheront ici.</p>
                          </div>
                        </div>
                      </Alert>
                    )}
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Motif (Optionnel)</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="motif"
                      value={rdvForm.motif}
                      onChange={handleRdvChange}
                      rows={3}
                      className="rounded-3 shadow-sm"
                      placeholder="Décrivez brièvement le motif de votre consultation..."
                    />
                  </Form.Group>
                  
                  <div className="d-flex justify-content-between flex-wrap">
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                      <StyledButton 
                        type="button"
                        className="secondary"
                        onClick={() => setStep(2)}
                      >
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Retour
                      </StyledButton>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                      <StyledButton 
                        type="submit"
                        className="primary"
                        disabled={!rdvForm.medecinId || !rdvForm.heure}
                      >
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Confirmer le rendez-vous
                      </StyledButton>
                    </motion.div>
                  </div>
                </Form>
              </CardBody>
            </StyledCard>
          </motion.div>
        )}
        
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StyledCard>
              <CardHeader>
                <h4 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon={faCheck} className="me-2 text-success" />
                  Rendez-vous confirmé
                </h4>
              </CardHeader>
              <CardBody className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 300,
                    damping: 15
                  }}
                  className="mb-4"
                >
                  <FontAwesomeIcon icon={faCheck} size="4x" className="text-success" />
                </motion.div>
                
                <h3 className="mb-4">Merci, {patientForm.nom}!</h3>
<p>Votre rendez-vous a été confirmé pour aujourd'hui à {rdvForm.heure} avec:</p>
<h5 className="mb-4">
  {(() => {
    const medecin = medecins.find(m => m.id === rdvForm.medecinId);
    return medecin ? `${medecin.titre || "Dr"} ${medecin.nom}` : "";
  })()}
</h5>

                {orderNumber !== null && (
                  <div className="mb-4">
                    <h5>Votre numéro d'ordre:</h5>
                    <div className="display-4 fw-bold text-primary">{orderNumber}</div>
                  </div>
                )}
                
                <div className="mb-4">
                  <h5>Votre code de vérification:</h5>
                  <VerificationCode>{verificationCode}</VerificationCode>
                  <p className="text-muted">Conservez ce code pour vérifier votre rendez-vous à l'accueil</p>
                </div>
                
                <Alert variant="info" className="mb-4 text-start">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-3 fs-4" />
                    <div>
                      <p className="mb-0">
                        Veuillez vous présenter à l'accueil 30 minutes avant votre rendez-vous.
                      </p>
                    </div>
                  </div>
                </Alert>
                
                <div className="d-flex justify-content-between flex-wrap">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                    <StyledButton 
                      className="secondary"
                      onClick={() => setShowVerificationModal(true)}
                    >
                      <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
                      Vérifier un rendez-vous
                    </StyledButton>
                  </motion.div>
                  
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="mb-2">
                    <StyledButton 
                      className="primary"
                      onClick={() => navigate("/")}
                    >
                      Terminer
                    </StyledButton>
                  </motion.div>
                </div>
              </CardBody>
            </StyledCard>
          </motion.div>
        )}
        
        {/* Modal de vérification de rendez-vous */}
        {renderVerificationModal()}
      </Container>
    </PageWrapper>
  );
};

export default PatientQRLanding;

